/**
 * Admin Media & Press manager.
 *
 * Full CRUD over `db.media_items`. Lists every item (drafts and
 * published, all categories) sorted by featured → sort_order → newest.
 * Click any row to edit; "+ New item" opens the same drawer empty.
 *
 * Two upload slots per item:
 *   • Media asset — video (mp4/mov/webm), PDF, or image, depending on `kind`
 *   • Cover image — poster shown on cards & social previews
 * For `video_embed` items, no upload is needed — paste a YouTube /
 * Vimeo URL into `embed_url` instead.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PlusIcon as Plus, PencilSimpleIcon as PencilSimple, TrashIcon as Trash, UploadSimpleIcon as UploadSimple, StarIcon as Star, EyeIcon as Eye, EyeSlashIcon as EyeSlash, ShareNetworkIcon as ShareNetwork, FilmStripIcon as FilmStrip, FilePdfIcon as FilePdf, ImageIcon as ImageIcon, PlayCircleIcon as PlayCircle, XIcon as XIcon, CopyIcon as Copy, LinkIcon as LinkIcon } from "@phosphor-icons/react";

const CATEGORIES = [
  { value: "promo_video",  label: "Promo Video" },
  { value: "flyer",        label: "Flyer" },
  { value: "testimonial",  label: "Testimonial" },
  { value: "how_to",       label: "How-To" },
  { value: "press",        label: "Press" },
  { value: "other",        label: "Other" },
];

const KINDS = [
  { value: "video_file",   label: "Self-hosted video (mp4/mov/webm)" },
  { value: "video_embed",  label: "YouTube / Vimeo embed (paste URL)" },
  { value: "pdf",          label: "PDF (flyer / one-pager)" },
  { value: "image",        label: "Image (jpg/png/webp)" },
];

const EMPTY = {
  category: "promo_video",
  kind: "video_embed",
  title: "",
  subtitle: "",
  description: "",
  cover_url: "",
  media_url: "",
  embed_url: "",
  cta_label: "",
  cta_url: "",
  featured: false,
  sort_order: 0,
  published: true,
  slug: "",
};

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

export default function AdminMedia() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | item

  const reload = () => {
    api.get("/admin/media")
      .then((r) => setItems(r.data?.items || []))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load media");
        setItems([]);
      });
  };
  useEffect(() => { reload(); }, []);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 font-manrope text-ink">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Admin · Content</p>
            <h1 className="font-spectral text-3xl tracking-tight mt-1">Media & Press</h1>
            <p className="text-sm text-[#666] mt-2 max-w-2xl">
              Everything that appears at <Link to="/media" className="underline">/media</Link> and on the landing-page Media strip. Featured items show on the landing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" data-testid="admin-media-open-library">
              <Link to="/media" target="_blank" rel="noreferrer">
                <Eye size={16} className="mr-2" /> View public page
              </Link>
            </Button>
            <Button onClick={() => setEditing({ ...EMPTY, _new: true })} data-testid="admin-media-new-btn">
              <Plus size={16} className="mr-2" /> New item
            </Button>
          </div>
        </div>

        <div className="mt-8" data-testid="admin-media-list">
          {items === null ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center">
              <FilmStrip size={32} className="mx-auto text-[#9b9b9b]" />
              <p className="mt-3 font-spectral text-xl">No media yet — add your first item.</p>
              <p className="mt-1 text-sm text-[#666]">
                Drop a YouTube link, upload an MP4, or attach a flyer PDF.
              </p>
              <Button className="mt-4" onClick={() => setEditing({ ...EMPTY, _new: true })} data-testid="admin-media-empty-new">
                <Plus size={16} className="mr-2" /> Add first item
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((it) => (
                <Row
                  key={it.id}
                  item={it}
                  onEdit={() => setEditing(it)}
                  onChange={reload}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditorDrawer
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { reload(); setEditing(null); }}
        />
      )}
    </AppShell>
  );
}

function kindIcon(kind) {
  if (kind === "video_file" || kind === "video_embed") return FilmStrip;
  if (kind === "pdf") return FilePdf;
  if (kind === "image") return ImageIcon;
  return PlayCircle;
}

function Row({ item, onEdit, onChange }) {
  const Icon = kindIcon(item.kind);
  const cover = resolveUrl(item.cover_url);
  const publicUrl = `${webOrigin()}/media/${item.slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public link copied");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/media/${item.id}`);
      toast.success("Deleted");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`admin-media-row-${item.slug}`}>
      <div className="w-28 aspect-video rounded-md overflow-hidden bg-[#F4F4F4] shrink-0 relative">
        {cover ? (
          <img src={cover} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#0E0E0E]">
            <Icon size={24} className="text-gold" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="flex flex-wrap items-center gap-2">
          {item.featured && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded-full bg-gold/15 text-gold px-2 py-0.5 font-semibold">
              <Star size={10} weight="fill" /> Featured
            </span>
          )}
          {!item.published && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded-full bg-[#F4F4F4] text-[#666] px-2 py-0.5">
              <EyeSlash size={10} /> Draft
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wider text-[#888]">
            {categoryLabel(item.category)} · {kindLabel(item.kind)}
          </span>
        </div>
        <p className="font-spectral text-lg mt-1 leading-tight">{item.title}</p>
        {item.subtitle && <p className="text-sm text-[#666] mt-0.5 line-clamp-1">{item.subtitle}</p>}
        <p className="text-xs text-[#888] mt-1 flex items-center gap-1.5">
          <LinkIcon size={12} /> /media/{item.slug}
          {item.view_count > 0 && <span>· {item.view_count} views</span>}
          {item.share_count > 0 && <span>· {item.share_count} shares</span>}
          {item.download_count > 0 && <span>· {item.download_count} downloads</span>}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={copyLink} data-testid={`admin-media-copy-${item.slug}`}>
          <Copy size={14} className="mr-1.5" /> Copy link
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`admin-media-edit-${item.slug}`}>
          <PencilSimple size={14} className="mr-1.5" /> Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={remove} className="text-[#C1272D]" data-testid={`admin-media-delete-${item.slug}`}>
          <Trash size={14} />
        </Button>
      </div>
    </div>
  );
}

function EditorDrawer({ initial, onClose, onSaved }) {
  const isNew = !!initial._new;
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const mediaInput = useRef(null);
  const coverInput = useRef(null);

  // After we save a brand-new item we need the resulting `id` so we
  // can attach an upload to it. The save endpoint returns the doc.
  const [createdId, setCreatedId] = useState(isNew ? null : initial.id);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        kind: form.kind,
        title: form.title,
        subtitle: form.subtitle || "",
        description: form.description || "",
        cover_url: form.cover_url || "",
        media_url: form.media_url || "",
        embed_url: form.embed_url || "",
        cta_label: form.cta_label || "",
        cta_url: form.cta_url || "",
        featured: !!form.featured,
        sort_order: Number(form.sort_order) || 0,
        published: !!form.published,
        slug: form.slug || null,
      };
      let saved;
      if (isNew && !createdId) {
        // First save creates the record so we can attach uploads.
        // Backend requires `media_url` for non-embed kinds; for new
        // items with kind in {video_file, pdf, image} the user is
        // expected to upload below, so we provisionally set
        // `media_url` to a sentinel and immediately overwrite via
        // the upload endpoint. To keep validation strict for true
        // misuse, we only accept this when the user proceeds to
        // upload.
        if (
          ["video_file", "pdf", "image"].includes(form.kind) &&
          !payload.media_url
        ) {
          // Allow create-without-asset by temporarily flipping the
          // item to a draft + embed-kind placeholder; the row will
          // appear with a "needs asset" hint and the editor stays
          // open for the upload step. Far cleaner than refusing the
          // create.
          toast.message("Saved as draft — upload the file next.");
          payload.published = false;
          payload.kind = "video_embed";
          payload.embed_url = "https://www.youtube.com/embed/dQw4w9WgXcQ"; // placeholder, will be overwritten when file uploaded
        }
        const res = await api.post("/admin/media", payload);
        saved = res.data;
        setCreatedId(saved.id);
        setForm((f) => ({ ...f, ...saved, _new: false }));
        toast.success("Created — keep editing or attach files below.");
      } else {
        const id = createdId || initial.id;
        const res = await api.put(`/admin/media/${id}`, payload);
        saved = res.data;
        setForm((f) => ({ ...f, ...saved }));
        toast.success("Saved");
        onSaved();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (file, slot) => {
    const id = createdId || initial.id;
    if (!id) { toast.error("Save the item first to attach files"); return; }
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const url = slot === "cover" ? `/admin/media/${id}/cover` : `/admin/media/${id}/asset`;
      const res = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (slot === "cover") {
        setForm((f) => ({ ...f, cover_url: res.data.cover_url }));
      } else {
        setForm((f) => ({ ...f, media_url: res.data.media_url, kind: f.kind === "video_embed" && initial._new ? f.kind : f.kind }));
      }
      toast.success("Upload complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const needsUpload = ["video_file", "pdf", "image"].includes(form.kind);
  const needsEmbed = form.kind === "video_embed";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div
        className="min-h-full md:min-h-0 md:max-w-3xl md:mx-auto md:my-10 bg-white md:rounded-2xl shadow-xl p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-media-editor"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F4F4F4]"
          data-testid="admin-media-editor-close"
          aria-label="Close"
        >
          <XIcon size={18} />
        </button>

        <h2 className="font-spectral text-2xl">{isNew && !createdId ? "New media item" : `Edit: ${form.title || "Untitled"}`}</h2>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <Field label="Category">
            <select
              className="w-full rounded-md border border-[#E5E5E5] px-3 py-2 text-sm bg-white"
              value={form.category}
              onChange={(e) => set("category")(e.target.value)}
              data-testid="admin-media-field-category"
            >
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Kind">
            <select
              className="w-full rounded-md border border-[#E5E5E5] px-3 py-2 text-sm bg-white"
              value={form.kind}
              onChange={(e) => set("kind")(e.target.value)}
              data-testid="admin-media-field-kind"
            >
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Title" className="mt-4">
          <Input
            value={form.title}
            onChange={(e) => set("title")(e.target.value)}
            placeholder="e.g. The Armstrong K9 Story — 60-second pitch"
            data-testid="admin-media-field-title"
          />
        </Field>
        <Field label="Subtitle / one-line summary" className="mt-3">
          <Input
            value={form.subtitle}
            onChange={(e) => set("subtitle")(e.target.value)}
            placeholder="Shown under the title on cards and detail pages"
            data-testid="admin-media-field-subtitle"
          />
        </Field>
        <Field label="Description (optional, plain text or paragraphs)" className="mt-3">
          <Textarea
            value={form.description}
            onChange={(e) => set("description")(e.target.value)}
            rows={4}
            placeholder="Long-form context shown on the detail page"
            data-testid="admin-media-field-description"
          />
        </Field>

        {needsEmbed && (
          <Field label="YouTube or Vimeo URL" className="mt-3">
            <Input
              value={form.embed_url}
              onChange={(e) => set("embed_url")(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…  or  https://vimeo.com/…"
              data-testid="admin-media-field-embed-url"
            />
          </Field>
        )}

        {/* Uploads — only after the row exists */}
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <UploadSlot
            label={needsUpload ? "Media file (video / PDF / image)" : "Optional video preview file"}
            value={form.media_url}
            disabled={!createdId}
            disabledReason="Save the item first to attach a file"
            onPick={() => mediaInput.current?.click()}
            inputRef={mediaInput}
            accept={
              form.kind === "video_file" ? "video/*"
              : form.kind === "pdf" ? "application/pdf"
              : form.kind === "image" ? "image/*"
              : "video/*,application/pdf,image/*"
            }
            onFile={(f) => uploadAsset(f, "asset")}
            testid="admin-media-upload-asset"
          />
          <UploadSlot
            label="Cover image (poster shown on cards)"
            value={form.cover_url}
            disabled={!createdId}
            disabledReason="Save the item first to attach a cover"
            onPick={() => coverInput.current?.click()}
            inputRef={coverInput}
            accept="image/*"
            onFile={(f) => uploadAsset(f, "cover")}
            testid="admin-media-upload-cover"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <Field label="CTA button label (optional)">
            <Input
              value={form.cta_label}
              onChange={(e) => set("cta_label")(e.target.value)}
              placeholder="e.g. Sign up free"
              data-testid="admin-media-field-cta-label"
            />
          </Field>
          <Field label="CTA URL (optional)">
            <Input
              value={form.cta_url}
              onChange={(e) => set("cta_url")(e.target.value)}
              placeholder="https://armstrongk9registry.com/register"
              data-testid="admin-media-field-cta-url"
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <Field label="Slug (URL)">
            <Input
              value={form.slug}
              onChange={(e) => set("slug")(e.target.value)}
              placeholder="auto-generated from title"
              data-testid="admin-media-field-slug"
            />
          </Field>
          <Field label="Sort order (lower shows first)">
            <Input
              type="number"
              value={form.sort_order}
              onChange={(e) => set("sort_order")(e.target.value)}
              data-testid="admin-media-field-sort"
            />
          </Field>
          <div className="flex flex-col gap-3 pt-1">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Featured on landing</span>
              <Switch
                checked={!!form.featured}
                onCheckedChange={(v) => set("featured")(v)}
                data-testid="admin-media-field-featured"
              />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Published</span>
              <Switch
                checked={!!form.published}
                onCheckedChange={(v) => set("published")(v)}
                data-testid="admin-media-field-published"
              />
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 mt-8 pt-5 border-t border-[#EEE]">
          <Button variant="ghost" onClick={onClose} data-testid="admin-media-editor-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="admin-media-editor-save">
            {saving ? "Saving…" : (createdId ? "Save changes" : "Create item")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-[#666]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function UploadSlot({ label, value, onPick, inputRef, accept, onFile, testid, disabled, disabledReason }) {
  const url = resolveUrl(value);
  return (
    <div data-testid={`${testid}-slot`}>
      <Label className="text-xs uppercase tracking-wider text-[#666]">{label}</Label>
      <div className="mt-1.5 border border-dashed border-[#D5D5D5] rounded-lg p-3 bg-[#FAFAFA]">
        {url ? (
          <div className="flex items-center gap-3">
            {/\.(jpg|jpeg|png|webp|gif)$/i.test(url) ? (
              <img src={url} alt="" className="h-16 w-16 object-cover rounded-md bg-[#F4F4F4]" />
            ) : (
              <div className="h-16 w-16 rounded-md bg-[#0E0E0E] flex items-center justify-center">
                <FilmStrip size={20} className="text-gold" />
              </div>
            )}
            <div className="text-xs text-[#666] flex-1 break-all">{value}</div>
          </div>
        ) : (
          <p className="text-xs text-[#888]">No file attached yet.</p>
        )}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          onChange={(e) => onFile?.(e.target.files?.[0])}
          data-testid={`${testid}-input`}
        />
        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          onClick={onPick}
          disabled={disabled}
          data-testid={`${testid}-btn`}
        >
          <UploadSimple size={14} className="mr-1.5" />
          {value ? "Replace file" : "Upload file"}
        </Button>
        {disabled && (
          <p className="text-[11px] text-[#a00] mt-2">{disabledReason}</p>
        )}
      </div>
    </div>
  );
}

function categoryLabel(c) { return CATEGORIES.find((x) => x.value === c)?.label || c; }
function kindLabel(k) { return KINDS.find((x) => x.value === k)?.label?.split(" ")[0] || k; }
